import { useState, useEffect } from "react";
import { API, useAuth, authFetch, useEditMode } from "@/App";
import { toast } from "sonner";
import { SyncNowButton } from "@/components/SyncNowButton";
import { RegionSwitcher } from "@/components/RegionSwitcher";
import { useRegionFilter } from "@/hooks/useRegionFilter";
import { mutate, confirmDelete } from "@/lib/apiMutate";
import {
  Store, Plus, Search, Check, X, MoreHorizontal, Edit, Phone,
  MapPin, CreditCard, AlertCircle, Navigation, Upload, FileText,
  ChevronRight, ChevronLeft, Building2, User, Landmark, Shield,
  FileCheck, Trash2, QrCode, Copy, MapPinPlus, Download, Gift, BookOpen,
} from "lucide-react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import DynamicFormRenderer from "@/components/DynamicFormRenderer";
import DealerLoyaltyCard from "@/components/DealerLoyaltyCard";
import { DealerCatalogueDialog } from "@/components/DealerCatalogueDialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";

// ── File Upload Component ──
const FileUploadField = ({ label, docType, partyId, existingPath, onUploaded }) => {
  const [uploading, setUploading] = useState(false);
  const [fileName, setFileName] = useState(existingPath ? "Uploaded" : "");

  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 10 * 1024 * 1024) { toast.error("File too large (max 10MB)"); return; }
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append("file", file);
      const url = partyId
        ? `${API}/parties/${partyId}/upload-doc?doc_type=${docType}`
        : `${API}/upload?category=dealer-${docType}`;
      const res = await authFetch(url, { method: "POST", body: formData });
      const data = await res.json();
      if (data.success) { setFileName(file.name); onUploaded?.(data.data.storage_path); toast.success(`${label} uploaded`); }
      else toast.error(data.error || "Upload failed");
    } catch { toast.error("Upload failed"); }
    finally { setUploading(false); }
  };

  return (
    <div data-testid={`upload-${docType}`}>
      <label className="text-sm font-medium text-slate-700 block mb-1">{label}</label>
      <label className={`flex items-center gap-2 px-3 py-2 border rounded-md cursor-pointer transition
        ${existingPath || fileName ? "border-emerald-300 bg-emerald-50" : "border-slate-200 hover:border-slate-300 bg-white"}`}>
        {uploading ? <div className="w-4 h-4 spinner" /> : existingPath || fileName ? <FileCheck className="w-4 h-4 text-emerald-600 shrink-0" /> : <Upload className="w-4 h-4 text-slate-400 shrink-0" />}
        <span className="text-sm truncate text-slate-600">{uploading ? "Uploading..." : fileName || (existingPath ? "Document uploaded" : "Choose file...")}</span>
        <input type="file" className="hidden" onChange={handleUpload} disabled={uploading} accept=".jpg,.jpeg,.png,.pdf,.webp" />
      </label>
    </div>
  );
};

// ── Step indicators for BA form ──
const STEPS = [
  { key: "business", label: "Business Details", icon: Building2 },
  { key: "owner", label: "Owner / Proprietor", icon: User },
  { key: "bank", label: "Bank Details", icon: Landmark },
  { key: "documents", label: "Documents", icon: FileText },
  { key: "declaration", label: "Declaration", icon: Shield },
];

const StepIndicator = ({ current, onStepClick }) => (
  <div className="flex items-center gap-1 mb-6" data-testid="step-indicator">
    {STEPS.map((step, i) => {
      const Icon = step.icon;
      const isActive = i === current;
      const isDone = i < current;
      return (
        <div key={step.key} className="flex items-center flex-1">
          <button type="button" onClick={() => onStepClick(i)}
            className={`flex items-center gap-1.5 px-2 py-1.5 rounded-md text-xs font-medium transition w-full
              ${isActive ? "bg-[#8B1A1A] text-white" : isDone ? "bg-emerald-100 text-emerald-700" : "bg-slate-100 text-slate-400"}`}
            data-testid={`step-${step.key}`}>
            <Icon className="w-3.5 h-3.5 shrink-0" />
            <span className="hidden sm:inline truncate">{step.label}</span>
            <span className="sm:hidden">{i + 1}</span>
          </button>
          {i < STEPS.length - 1 && <ChevronRight className="w-3 h-3 text-slate-300 shrink-0 mx-0.5" />}
        </div>
      );
    })}
  </div>
);

// ── Initial Form Data ──
const INITIAL_FORM = {
  firm_name: "", contact_person: "", mobile_number: "", email: "",
  party_type: "DEALER", address: "", gstin: "", pan: "", category: "B",
  credit_limit: 0, district: "", region_id: "",
  year_of_establishment: "", entity_type: "",
  father_husband_name: "", residence_address: "",
  taluka: "", state: "Gujarat", telephone_residence: "",
  owner_aadhar_number: "", owner_pan_number: "", owner_blood_group: "",
  owner_home_address: "", google_location: "",
  bank_name: "", bank_account_number: "", bank_account_type: "",
  bank_branch: "", ifsc_code: "", bank_signatory_name: "",
  declaration_accepted: false, declaration_date: "",
  notes: "", tags: [],
  delivery_addresses: [{ label: "Primary", address: "", is_default: true }],
};

const QUICK_FORM_INITIAL = {
  firm_name: "", contact_person: "", mobile_number: "", email: "",
  address: "", google_location: "", party_type: "DEALER", region_id: "", district: "",
  delivery_addresses: [{ label: "Primary", address: "", is_default: true }],
};

// ── Main Page ──
const DealersPage = () => {
  const { user } = useAuth();
  const canEdit = useEditMode();
  const isSuperAdmin = user?.role === "SUPER_ADMIN";
  const { activeRegion, setActiveRegion, filterByRegion } = useRegionFilter();
  const [parties, setParties] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [typeFilter, setTypeFilter] = useState("all");
  const [statusFilter, setStatusFilter] = useState("all");
  const [regionFilter, setRegionFilter] = useState(user?.region_id || "all");
  // BA Form state
  const [isBAOpen, setIsBAOpen] = useState(false);
  const [activeEditTab, setActiveEditTab] = useState("basic"); // basic | ba-form
  const [editingParty, setEditingParty] = useState(null);
  const [formData, setFormData] = useState({ ...INITIAL_FORM });
  const [step, setStep] = useState(0);
  const [docPaths, setDocPaths] = useState({});
  const [savedPartyId, setSavedPartyId] = useState(null);
  const [basicSaving, setBasicSaving] = useState(false);
  // Quick form state
  const [isQuickOpen, setIsQuickOpen] = useState(false);
  const [quickForm, setQuickForm] = useState({ ...QUICK_FORM_INITIAL });
  // Profile state
  const [selectedParty, setSelectedParty] = useState(null);
  const [profileOpen, setProfileOpen] = useState(false);
  // QR dialog
  const [qrParty, setQrParty] = useState(null);
  // Loyalty dialog
  const [loyaltyParty, setLoyaltyParty] = useState(null);
  // Catalogue dialog (P2 iter81)
  const [cataloguePartyId, setCataloguePartyId] = useState(null);

  useEffect(() => { fetchParties(); }, [search, typeFilter, statusFilter, regionFilter]);

  const fetchParties = async () => {
    try {
      let url = `${API}/parties?`;
      if (search) url += `search=${search}&`;
      if (typeFilter && typeFilter !== "all") url += `party_type=${typeFilter}&`;
      if (statusFilter && statusFilter !== "all") url += `approval_status=${statusFilter}&`;
      if (regionFilter && regionFilter !== "all") url += `region=${regionFilter}&`;
      const response = await authFetch(url);
      const data = await response.json();
      if (data.success) setParties(data.data);
    } catch (error) { console.error("Failed to fetch parties:", error); }
    finally { setLoading(false); }
  };

  // ── Quick Add Dealer ──
  const handleQuickSubmit = async (e) => {
    e.preventDefault();
    if (!quickForm.firm_name) { toast.error("Firm name is required"); return; }
    const payload = {
      ...quickForm,
      registration_type: "QUICK",
      credit_limit: 0,
      category: "B",
    };
    const r = await mutate(
      authFetch,
      `${API}/parties`,
      { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify(payload) },
      { successMessage: "Dealer added successfully", failureMessage: "Failed to add dealer" },
    );
    if (!r.ok) return;
    setIsQuickOpen(false);
    setQuickForm({ ...QUICK_FORM_INITIAL });
    fetchParties();
  };

  const addDeliveryAddress = () => {
    setQuickForm((f) => ({
      ...f,
      delivery_addresses: [...f.delivery_addresses, { label: `Address ${f.delivery_addresses.length + 1}`, address: "", is_default: false }],
    }));
  };

  const removeDeliveryAddress = (idx) => {
    setQuickForm((f) => ({
      ...f,
      delivery_addresses: f.delivery_addresses.filter((_, i) => i !== idx),
    }));
  };

  const updateDeliveryAddress = (idx, field, value) => {
    setQuickForm((f) => {
      const addrs = [...f.delivery_addresses];
      addrs[idx] = { ...addrs[idx], [field]: value };
      return { ...f, delivery_addresses: addrs };
    });
  };

  // ── BA Form Submit ──
  const handleBASubmit = async () => {
    const url = editingParty ? `${API}/parties/${editingParty.party_id}` : `${API}/parties`;
    const method = editingParty ? "PUT" : "POST";
    const payload = { ...formData, credit_limit: parseInt(formData.credit_limit) * 100, registration_type: "FULL" };
    Object.entries(docPaths).forEach(([k, v]) => { payload[k] = v; });

    const r = await mutate(
      authFetch,
      url,
      { method, headers: { "Content-Type": "application/json" }, body: JSON.stringify(payload) },
      { silentSuccess: true, failureMessage: "Failed to save party" },
    );
    if (!r.ok) return null;

    const saved = r.data?.data ?? r.data;
    const pid = saved?.party_id;
    setSavedPartyId(pid);
    if (step < STEPS.length - 1) {
      toast.success("Saved! Continue to next step.");
      if (!editingParty && pid) setEditingParty(saved);
    } else {
      toast.success(editingParty ? "Party updated" : "Application submitted");
      closeBA();
      fetchParties();
    }
    return saved;
  };

  const handleDeleteParty = async (party) => {
    const r = await confirmDelete(authFetch, `${API}/parties/${party.party_id}/deactivate`, {
      promptMessage: `Deactivate dealer "${party.firm_name}"? This is reversible from the audit log.`,
      successMessage: "Dealer deactivated",
      failureMessage: "Failed to deactivate dealer",
      onSuccess: () => setParties((prev) => prev.filter((p) => p.party_id !== party.party_id)),
    });
    if (r.ok) fetchParties();
  };

  const handleNext = async () => {
    if (step === 0 && !formData.firm_name) { toast.error("Firm name is required"); return; }
    const saved = await handleBASubmit();
    if (saved) setStep((s) => Math.min(s + 1, STEPS.length - 1));
  };

  const handleFinalSubmit = async () => {
    if (!formData.declaration_accepted) { toast.error("Please accept the declaration"); return; }
    await handleBASubmit();
  };

  const handleApprove = async (partyId) => {
    try {
      const res = await authFetch(`${API}/parties/${partyId}/approve`, { method: "POST" });
      const data = await res.json();
      if (data.success) { toast.success("Party approved"); fetchParties(); }
    } catch { toast.error("Failed to approve"); }
  };
  const handleReject = async (partyId) => {
    try {
      const res = await authFetch(`${API}/parties/${partyId}/reject`, { method: "POST" });
      const data = await res.json();
      if (data.success) { toast.success("Party rejected"); fetchParties(); }
    } catch { toast.error("Failed to reject"); }
  };

  const openEditDialog = (party) => {
    setEditingParty(party);
    setFormData({
      firm_name: party.firm_name || "", contact_person: party.contact_person || "",
      mobile_number: party.mobile_number || "", email: party.email || "",
      party_type: party.party_type || "DEALER", address: party.address || "",
      gstin: party.gstin || "", pan: party.pan || "", category: party.category || "B",
      credit_limit: (party.credit_limit || 0) / 100, district: party.district || "",
      region_id: party.region_id || "",
      year_of_establishment: party.year_of_establishment || "",
      entity_type: party.entity_type || "",
      father_husband_name: party.father_husband_name || "",
      residence_address: party.residence_address || "",
      taluka: party.taluka || "", state: party.state || "Gujarat",
      telephone_residence: party.telephone_residence || "",
      owner_aadhar_number: party.owner_aadhar_number || "",
      owner_pan_number: party.owner_pan_number || "",
      owner_blood_group: party.owner_blood_group || "",
      owner_home_address: party.owner_home_address || "",
      google_location: party.google_location || "",
      bank_name: party.bank_name || "", bank_account_number: party.bank_account_number || "",
      bank_account_type: party.bank_account_type || "",
      bank_branch: party.bank_branch || "", ifsc_code: party.ifsc_code || "",
      bank_signatory_name: party.bank_signatory_name || "",
      declaration_accepted: party.declaration_accepted || false,
      declaration_date: party.declaration_date || "",
      notes: party.notes || "",
      tags: party.tags || [],
      delivery_addresses: party.delivery_addresses && party.delivery_addresses.length ? party.delivery_addresses : [{ label: "Primary", address: "", is_default: true }],
    });
    setDocPaths({});
    setSavedPartyId(party.party_id);
    setStep(0);
    setActiveEditTab("basic");
    setIsBAOpen(true);
  };

  const openBAForQuickDealer = (party) => {
    setProfileOpen(false);
    openEditDialog(party);
    setActiveEditTab("ba-form");
  };

  const closeBA = () => {
    setIsBAOpen(false); setEditingParty(null); setFormData({ ...INITIAL_FORM }); setStep(0); setDocPaths({}); setSavedPartyId(null); setActiveEditTab("basic");
  };

  // ── Basic details save (tab 1) ──
  const saveBasicDetails = async () => {
    if (!formData.firm_name) { toast.error("Firm name is required"); return; }
    setBasicSaving(true);
    try {
      const url = editingParty ? `${API}/parties/${editingParty.party_id}` : `${API}/parties`;
      const method = editingParty ? "PUT" : "POST";
      const payload = {
        firm_name: formData.firm_name,
        contact_person: formData.contact_person,
        mobile_number: formData.mobile_number,
        email: formData.email,
        party_type: formData.party_type,
        address: formData.address,
        district: formData.district,
        region_id: formData.region_id,
        category: formData.category,
        credit_limit: parseInt(formData.credit_limit || 0) * 100,
        google_location: formData.google_location,
        notes: formData.notes,
        tags: formData.tags,
        delivery_addresses: formData.delivery_addresses,
      };
      const res = await authFetch(url, {
        method, headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });
      const data = await res.json();
      if (data.success) {
        toast.success("Basic details saved");
        if (!editingParty && data.data?.party_id) {
          setEditingParty(data.data);
          setSavedPartyId(data.data.party_id);
        }
        fetchParties();
      } else {
        toast.error(data.error || "Save failed");
      }
    } catch { toast.error("Network error"); }
    finally { setBasicSaving(false); }
  };

  // ── Download BA Form as PDF ──
  const downloadBAFormPDF = () => {
    const pid = editingParty?.party_id || savedPartyId;
    if (!pid) { toast.error("Save the dealer first"); return; }
    const printWindow = window.open("", "_blank", "width=900,height=1100");
    if (!printWindow) return;
    const f = formData;
    const html = `<!DOCTYPE html><html><head><title>Business Associate Form - ${f.firm_name}</title>
<style>
  @page { size: A4; margin: 15mm; }
  body { font-family: Arial, sans-serif; font-size: 11px; color: #111; padding: 0; margin: 0; }
  .header { text-align: center; border-bottom: 3px double #8B1A1A; padding-bottom: 10px; margin-bottom: 16px; }
  .header h1 { color: #8B1A1A; font-size: 22px; margin: 0; letter-spacing: 1px; }
  .header p { margin: 2px 0; font-size: 10px; color: #444; }
  .section-title { background: #8B1A1A; color: #fff; padding: 5px 10px; font-weight: bold; font-size: 12px; margin: 14px 0 8px; text-transform: uppercase; letter-spacing: 0.5px; }
  .row { display: flex; gap: 12px; margin-bottom: 6px; }
  .cell { flex: 1; }
  .label { font-size: 9px; color: #666; text-transform: uppercase; }
  .value { font-size: 11px; font-weight: 600; border-bottom: 1px dotted #888; padding: 2px 0; min-height: 16px; }
  .instr { text-align: center; font-style: italic; color: #777; font-size: 10px; margin: 6px 0; }
  .declaration { border: 1px solid #8B1A1A; padding: 10px; background: #fcf7f7; margin-top: 14px; font-size: 10px; line-height: 1.5; }
  .sig-row { display: flex; justify-content: space-between; margin-top: 40px; gap: 40px; }
  .sig-box { flex: 1; text-align: center; border-top: 1px solid #000; padding-top: 5px; font-size: 10px; }
  table { width: 100%; border-collapse: collapse; margin-top: 5px; }
  th, td { border: 1px solid #888; padding: 4px 6px; font-size: 10px; text-align: left; }
  th { background: #f1d0d0; }
  .check-grid { display: grid; grid-template-columns: repeat(2, 1fr); gap: 4px; font-size: 10px; }
  .check-grid div::before { content: "\\2610  "; font-size: 12px; }
</style></head><body>
  <div class="header">
    <h1>AVI CEMENT LIMITED</h1>
    <p>D-1, Jay Tower, Ankur Commercial Centre, Nr. Ankur Cross Road, Naranpura, Ahmedabad-380013</p>
    <p>www.avicement.com &middot; sales@avicement.com</p>
    <p class="instr">BUSINESS ASSOCIATES APPLICATION &middot; Party ID: ${pid}</p>
  </div>
  <div class="instr">Please fill up form in CAPITAL LETTERS ONLY</div>

  <div class="section-title">Applicant Information</div>
  <div class="row">
    <div class="cell"><div class="label">Firm Name</div><div class="value">${f.firm_name||""}</div></div>
    <div class="cell"><div class="label">Year of Esta't</div><div class="value">${f.year_of_establishment||""}</div></div>
  </div>
  <div class="row">
    <div class="cell"><div class="label">PAN No.</div><div class="value">${f.pan||""}</div></div>
    <div class="cell"><div class="label">GST No.</div><div class="value">${f.gstin||""}</div></div>
    <div class="cell"><div class="label">Email ID</div><div class="value">${f.email||""}</div></div>
  </div>

  <div class="section-title">Business Category</div>
  <div class="check-grid">
    <div>${f.entity_type==="Proprietor"?"\u2611":""} Proprietor</div>
    <div>${f.entity_type==="Partner"?"\u2611":""} Partner</div>
    <div>${f.entity_type==="Director"?"\u2611":""} Director</div>
    <div>${f.entity_type==="Karta Key"?"\u2611":""} Karta Key</div>
    <div>${f.entity_type==="Contact Person"?"\u2611":""} Contact Person</div>
  </div>

  <div class="section-title">Applicant Details</div>
  <div class="row">
    <div class="cell"><div class="label">Name</div><div class="value">${f.contact_person||""}</div></div>
    <div class="cell"><div class="label">Father / Husband Name</div><div class="value">${f.father_husband_name||""}</div></div>
  </div>
  <div class="row">
    <div class="cell"><div class="label">Residence Address</div><div class="value">${f.residence_address||""}</div></div>
  </div>
  <div class="row">
    <div class="cell"><div class="label">Taluka</div><div class="value">${f.taluka||""}</div></div>
    <div class="cell"><div class="label">District</div><div class="value">${f.district||""}</div></div>
    <div class="cell"><div class="label">State</div><div class="value">${f.state||""}</div></div>
  </div>
  <div class="row">
    <div class="cell"><div class="label">Tele. No. (R)</div><div class="value">${f.telephone_residence||""}</div></div>
    <div class="cell"><div class="label">Mobile</div><div class="value">${f.mobile_number||""}</div></div>
    <div class="cell"><div class="label">Email</div><div class="value">${f.email||""}</div></div>
  </div>

  <div class="section-title">Details of Bank</div>
  <div class="row">
    <div class="cell"><div class="label">Name of the Bank</div><div class="value">${f.bank_name||""}</div></div>
    <div class="cell"><div class="label">Branch</div><div class="value">${f.bank_branch||""}</div></div>
  </div>
  <div class="row">
    <div class="cell"><div class="label">Type of A/c</div><div class="value">${f.bank_account_type||""}</div></div>
    <div class="cell"><div class="label">A/c No.</div><div class="value">${f.bank_account_number||""}</div></div>
    <div class="cell"><div class="label">IFSC Code</div><div class="value">${f.ifsc_code||""}</div></div>
  </div>
  <div class="row">
    <div class="cell"><div class="label">Authorised Signatory</div><div class="value">${f.bank_signatory_name||""}</div></div>
  </div>
  <p style="font-size:9px;color:#666;margin-top:4px;">(Please attach last one month's bank statements)</p>

  <div class="section-title">List of Documents Attached</div>
  <div class="check-grid">
    <div>${editingParty?.partnership_deed_path?"\u2611":""} Partnership Deed / MoA / AoA</div>
    <div>${editingParty?.gst_certificate_path?"\u2611":""} GST Registration Copy</div>
    <div>${editingParty?.pan_card_path?"\u2611":""} PAN Card Copy</div>
    <div>${editingParty?.photo_id_path?"\u2611":""} Photo ID Proof</div>
    <div>${editingParty?.letterhead_path?"\u2611":""} Letter Head</div>
    <div>${editingParty?.bank_statement_path?"\u2611":""} Bank Statement (1 month)</div>
    <div>${editingParty?.cancelled_cheque_path?"\u2611":""} Cancelled Cheque</div>
    <div>${editingParty?.aadhar_card_path?"\u2611":""} Aadhar Card</div>
  </div>

  <div class="declaration">
    <strong>DECLARATION:</strong> I/We do hereby declare that the information furnished herein is correct to the best of my/our knowledge and belief. For any incorrect information/misinformation furnished and for non-compliance of the company's policies formulated from time to time, I/We agree that:
    <ol>
      <li>The company shall have the absolute right to reject my/our application for appointment as Business Associates / Sales Promoter.</li>
      <li>The company reserves the right to terminate my/our dealership without any notice and assigning any reason.</li>
      <li>The company shall have the right to forfeit or adjust the whole or part of my/our security deposit with them in the manner they may deem fit.</li>
    </ol>
    <p>Declaration ${f.declaration_accepted ? "ACCEPTED" : "Not yet accepted"} on ${f.declaration_date || "_______________"}</p>
  </div>

  <div class="sig-row">
    <div class="sig-box">Company Authorised Person Sign.</div>
    <div class="sig-box">Party Stamp &amp; Signature</div>
  </div>
</body></html>`;
    printWindow.document.write(html);
    printWindow.document.close();
    setTimeout(() => { printWindow.focus(); printWindow.print(); }, 400);
  };

  const formatCurrency = (paise) => new Intl.NumberFormat("en-IN", { style: "currency", currency: "INR", maximumFractionDigits: 0 }).format(paise / 100);

  const getApprovalBadge = (status) => {
    const s = { APPROVED: "bg-emerald-100 text-emerald-700", PENDING: "bg-amber-100 text-amber-700", REJECTED: "bg-red-100 text-red-700" };
    return <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${s[status] || "bg-slate-100 text-slate-600"}`}>{status}</span>;
  };

  const pendingParties = filterByRegion(parties, (p) => p.region_id).filter((p) => p.approval_status === "PENDING");
  const fv = (k, v) => setFormData((d) => ({ ...d, [k]: v }));

  const generateQRUrl = (party) => {
    const base = window.location.origin;
    return `${base}/api/webhooks/dealer-inquiry?prefill=${encodeURIComponent(JSON.stringify({ firm_name: party.firm_name, contact_person: party.contact_person, phone: party.mobile_number }))}`;
  };

  // ── Step Renderers ──
  const renderBusinessStep = () => (
    <div className="space-y-4">
      <p className="text-xs font-semibold text-[#8B1A1A] uppercase tracking-wide">Business Associates Application</p>
      <div className="grid grid-cols-2 gap-4">
        <div className="col-span-2">
          <label className="text-sm font-medium text-slate-700">Firm Name *</label>
          <Input value={formData.firm_name} onChange={(e) => fv("firm_name", e.target.value)} required data-testid="input-firm-name" />
        </div>
        <div><label className="text-sm font-medium text-slate-700">Year of Establishment</label>
          <Input value={formData.year_of_establishment} onChange={(e) => fv("year_of_establishment", e.target.value)} placeholder="e.g. 2015" data-testid="input-year-est" /></div>
        <div><label className="text-sm font-medium text-slate-700">Entity Type</label>
          <Select value={formData.entity_type || ""} onValueChange={(v) => fv("entity_type", v)}>
            <SelectTrigger data-testid="input-entity-type"><SelectValue placeholder="Select type" /></SelectTrigger>
            <SelectContent>
              {["Proprietor","Partner","Director","Karta Key","Contact Person"].map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}
            </SelectContent></Select></div>
        <div><label className="text-sm font-medium text-slate-700">PAN No.</label>
          <Input value={formData.pan} onChange={(e) => fv("pan", e.target.value.toUpperCase())} placeholder="ABCDE1234F" data-testid="input-pan" /></div>
        <div><label className="text-sm font-medium text-slate-700">GST No.</label>
          <Input value={formData.gstin} onChange={(e) => fv("gstin", e.target.value.toUpperCase())} data-testid="input-gstin" /></div>
        <div><label className="text-sm font-medium text-slate-700">Email</label>
          <Input type="email" value={formData.email} onChange={(e) => fv("email", e.target.value)} data-testid="input-email" /></div>
        <div><label className="text-sm font-medium text-slate-700">Party Type</label>
          <Select value={formData.party_type} onValueChange={(v) => fv("party_type", v)}>
            <SelectTrigger data-testid="input-type"><SelectValue /></SelectTrigger>
            <SelectContent>
              {["DEALER","DISTRIBUTOR","CONTRACTOR","BUILDER","END_USER"].map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}
            </SelectContent></Select></div>
        <div><label className="text-sm font-medium text-slate-700">Contact Person</label>
          <Input value={formData.contact_person} onChange={(e) => fv("contact_person", e.target.value)} data-testid="input-contact" /></div>
        <div><label className="text-sm font-medium text-slate-700">Mobile</label>
          <Input value={formData.mobile_number} onChange={(e) => fv("mobile_number", e.target.value)} data-testid="input-mobile" /></div>
        <div className="col-span-2"><label className="text-sm font-medium text-slate-700">Business Address</label>
          <Input value={formData.address} onChange={(e) => fv("address", e.target.value)} data-testid="input-address" /></div>
        <div><label className="text-sm font-medium text-slate-700">District</label>
          <Input value={formData.district} onChange={(e) => fv("district", e.target.value)} data-testid="input-district" /></div>
        <div><label className="text-sm font-medium text-slate-700">Region</label>
          <Select value={formData.region_id || ""} onValueChange={(v) => fv("region_id", v)}>
            <SelectTrigger data-testid="input-region"><SelectValue placeholder="Select region" /></SelectTrigger>
            <SelectContent><SelectItem value="GJ">Gujarat</SelectItem><SelectItem value="RJ">Rajasthan</SelectItem></SelectContent></Select></div>
        <div><label className="text-sm font-medium text-slate-700">Category</label>
          <Select value={formData.category} onValueChange={(v) => fv("category", v)}>
            <SelectTrigger data-testid="input-category"><SelectValue /></SelectTrigger>
            <SelectContent>{["A","B","C"].map(c => <SelectItem key={c} value={c}>Category {c}</SelectItem>)}</SelectContent></Select></div>
        <div><label className="text-sm font-medium text-slate-700">Credit Limit (INR)</label>
          <Input type="number" value={formData.credit_limit} onChange={(e) => fv("credit_limit", e.target.value)} data-testid="input-credit-limit" /></div>
      </div>
    </div>
  );

  const renderOwnerStep = () => (
    <div className="space-y-4">
      <p className="text-xs font-semibold text-[#8B1A1A] uppercase tracking-wide">Owner / Proprietor Details</p>
      <div className="grid grid-cols-2 gap-4">
        <div className="col-span-2"><label className="text-sm font-medium text-slate-700">Full Name (as per PAN)</label>
          <Input value={formData.contact_person} onChange={(e) => fv("contact_person", e.target.value)} data-testid="input-owner-name" /></div>
        <div><label className="text-sm font-medium text-slate-700">Father / Husband Name</label>
          <Input value={formData.father_husband_name} onChange={(e) => fv("father_husband_name", e.target.value)} data-testid="input-father-name" /></div>
        <div><label className="text-sm font-medium text-slate-700">Blood Group</label>
          <Select value={formData.owner_blood_group || ""} onValueChange={(v) => fv("owner_blood_group", v)}>
            <SelectTrigger data-testid="input-blood-group"><SelectValue placeholder="Select" /></SelectTrigger>
            <SelectContent>{["A+","A-","B+","B-","AB+","AB-","O+","O-"].map(bg => <SelectItem key={bg} value={bg}>{bg}</SelectItem>)}</SelectContent></Select></div>
        <div className="col-span-2"><label className="text-sm font-medium text-slate-700">Residence Address</label>
          <Input value={formData.residence_address} onChange={(e) => fv("residence_address", e.target.value)} data-testid="input-residence-addr" /></div>
        <div><label className="text-sm font-medium text-slate-700">Taluka</label><Input value={formData.taluka} onChange={(e) => fv("taluka", e.target.value)} data-testid="input-taluka" /></div>
        <div><label className="text-sm font-medium text-slate-700">State</label>
          <Select value={formData.state || "Gujarat"} onValueChange={(v) => fv("state", v)}>
            <SelectTrigger data-testid="input-state"><SelectValue /></SelectTrigger>
            <SelectContent>{["Gujarat","Rajasthan","Maharashtra","Madhya Pradesh"].map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent></Select></div>
        <div><label className="text-sm font-medium text-slate-700">Telephone (Res)</label><Input value={formData.telephone_residence} onChange={(e) => fv("telephone_residence", e.target.value)} data-testid="input-tel-res" /></div>
        <div><label className="text-sm font-medium text-slate-700">Mobile</label><Input value={formData.mobile_number} onChange={(e) => fv("mobile_number", e.target.value)} data-testid="input-owner-mobile" /></div>
        <div><label className="text-sm font-medium text-slate-700">Aadhar Number</label><Input value={formData.owner_aadhar_number} onChange={(e) => fv("owner_aadhar_number", e.target.value)} data-testid="input-aadhar" /></div>
        <div><label className="text-sm font-medium text-slate-700">PAN Number</label><Input value={formData.owner_pan_number} onChange={(e) => fv("owner_pan_number", e.target.value)} data-testid="input-owner-pan" /></div>
        <div><label className="text-sm font-medium text-slate-700">Google Location</label><Input value={formData.google_location} onChange={(e) => fv("google_location", e.target.value)} placeholder="Lat,Lng" data-testid="input-google-loc" /></div>
        <div><label className="text-sm font-medium text-slate-700">Home Address</label><Input value={formData.owner_home_address} onChange={(e) => fv("owner_home_address", e.target.value)} data-testid="input-home-addr" /></div>
      </div>
    </div>
  );

  const renderBankStep = () => (
    <div className="space-y-4">
      <p className="text-xs font-semibold text-[#8B1A1A] uppercase tracking-wide">Details of Bank</p>
      <div className="grid grid-cols-2 gap-4">
        <div><label className="text-sm font-medium text-slate-700">Bank Name</label><Input value={formData.bank_name} onChange={(e) => fv("bank_name", e.target.value)} data-testid="input-bank-name" /></div>
        <div><label className="text-sm font-medium text-slate-700">Branch</label><Input value={formData.bank_branch} onChange={(e) => fv("bank_branch", e.target.value)} data-testid="input-bank-branch" /></div>
        <div><label className="text-sm font-medium text-slate-700">Account Type</label>
          <Select value={formData.bank_account_type || ""} onValueChange={(v) => fv("bank_account_type", v)}>
            <SelectTrigger data-testid="input-account-type"><SelectValue placeholder="Select" /></SelectTrigger>
            <SelectContent>{["Savings","Current","Other"].map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent></Select></div>
        <div><label className="text-sm font-medium text-slate-700">Account No.</label><Input value={formData.bank_account_number} onChange={(e) => fv("bank_account_number", e.target.value)} data-testid="input-account-num" /></div>
        <div><label className="text-sm font-medium text-slate-700">IFSC Code</label><Input value={formData.ifsc_code} onChange={(e) => fv("ifsc_code", e.target.value.toUpperCase())} data-testid="input-ifsc" /></div>
        <div><label className="text-sm font-medium text-slate-700">Signatory Name</label><Input value={formData.bank_signatory_name} onChange={(e) => fv("bank_signatory_name", e.target.value)} data-testid="input-signatory" /></div>
      </div>
      <div className="p-3 bg-amber-50 border border-amber-200 rounded-md mt-4">
        <p className="text-xs text-amber-700">Please attach last one month's bank statements in the Documents step.</p>
      </div>
    </div>
  );

  const renderDocumentsStep = () => {
    const pid = editingParty?.party_id || savedPartyId;
    return (
      <div className="space-y-4">
        <p className="text-xs font-semibold text-[#8B1A1A] uppercase tracking-wide">List of Documents</p>
        {!pid && <div className="p-3 bg-amber-50 border border-amber-200 rounded-md"><p className="text-xs text-amber-700">Save business details first (Step 1) before uploading.</p></div>}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          {[
            ["Profile / Passport Photo","profile_photo","profile_photo_path"],
            ["PAN Card (all partners)","pan_card","pan_card_path"],
            ["GST Registration","gst_certificate","gst_certificate_path"],
            ["Aadhar Card","aadhar_card","aadhar_card_path"],
            ["Photo ID Proof","photo_id","photo_id_path"],
            ["Letter Head","letterhead","letterhead_path"],
            ["Partnership Deed / MoA","partnership_deed","partnership_deed_path"],
            ["Bank Statement (1 month)","bank_statement","bank_statement_path"],
            ["Cancelled Cheque","cancelled_cheque","cancelled_cheque_path"],
          ].map(([label, docType, pathKey]) => (
            <FileUploadField key={docType} label={label} docType={docType} partyId={pid}
              existingPath={editingParty?.[pathKey] || docPaths[pathKey]}
              onUploaded={(p) => setDocPaths((d) => ({ ...d, [pathKey]: p }))} />
          ))}
        </div>
      </div>
    );
  };

  const renderDeclarationStep = () => (
    <div className="space-y-4">
      <p className="text-xs font-semibold text-[#8B1A1A] uppercase tracking-wide">Declaration</p>
      <div className="p-4 bg-slate-50 border rounded-md text-sm text-slate-700 leading-relaxed space-y-3">
        <p>I/We do hereby declare that the information furnished herein is correct to the best of my/our knowledge and belief.</p>
        <ol className="list-decimal list-inside space-y-2 text-slate-600">
          <li>The company shall have the absolute right to reject my/our application.</li>
          <li>The company reserves the right to terminate my/our dealership without notice.</li>
          <li>The company may forfeit or adjust security deposits as deemed fit.</li>
        </ol>
      </div>
      <div className="flex items-start gap-3 mt-4">
        <Checkbox checked={formData.declaration_accepted} onCheckedChange={(c) => fv("declaration_accepted", !!c)} data-testid="declaration-checkbox" />
        <label className="text-sm text-slate-700 cursor-pointer" onClick={() => fv("declaration_accepted", !formData.declaration_accepted)}>
          I/We accept the declaration and agree to Avi Cement Limited terms.
        </label>
      </div>
      <div className="w-48"><label className="text-sm font-medium text-slate-700">Date</label>
        <Input type="date" value={formData.declaration_date} onChange={(e) => fv("declaration_date", e.target.value)} data-testid="input-declaration-date" /></div>
    </div>
  );

  // ── Delivery addresses inside edit form ──
  const updateEditDelivery = (i, field, value) => {
    setFormData(f => {
      const addrs = [...(f.delivery_addresses || [])];
      addrs[i] = { ...addrs[i], [field]: value };
      return { ...f, delivery_addresses: addrs };
    });
  };
  const addEditDelivery = () => {
    setFormData(f => ({
      ...f,
      delivery_addresses: [...(f.delivery_addresses || []), { label: `Address ${(f.delivery_addresses || []).length + 1}`, address: "", is_default: false }],
    }));
  };
  const removeEditDelivery = (i) => {
    setFormData(f => ({ ...f, delivery_addresses: (f.delivery_addresses || []).filter((_, idx) => idx !== i) }));
  };

  // Render Basic Details tab
  const renderBasicDetailsTab = () => {
    const pid = editingParty?.party_id || savedPartyId;
    return (
      <div className="space-y-4" data-testid="basic-details-tab">
        <div className="p-3 bg-blue-50 border border-blue-200 rounded-md">
          <p className="text-xs text-blue-700">Quick editable info. For full KYC with banking, declaration and documents, switch to the <strong>Business Associate Form</strong> tab.</p>
        </div>
        <div className="grid grid-cols-2 gap-4">
          <div className="col-span-2">
            <label className="text-sm font-medium text-slate-700">Firm Name *</label>
            <Input value={formData.firm_name} onChange={(e) => fv("firm_name", e.target.value)} required data-testid="basic-firm-name" />
          </div>
          <div>
            <label className="text-sm font-medium text-slate-700">Contact Person</label>
            <Input value={formData.contact_person} onChange={(e) => fv("contact_person", e.target.value)} data-testid="basic-contact" />
          </div>
          <div>
            <label className="text-sm font-medium text-slate-700">Mobile Number</label>
            <Input value={formData.mobile_number} onChange={(e) => fv("mobile_number", e.target.value)} data-testid="basic-mobile" />
          </div>
          <div>
            <label className="text-sm font-medium text-slate-700">Party Type</label>
            <Select value={formData.party_type} onValueChange={(v) => fv("party_type", v)}>
              <SelectTrigger data-testid="basic-type"><SelectValue /></SelectTrigger>
              <SelectContent>
                {["DEALER","DISTRIBUTOR","CONTRACTOR","BUILDER","END_USER"].map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}
              </SelectContent></Select>
          </div>
          <div>
            <label className="text-sm font-medium text-slate-700">Credit Limit (INR)</label>
            <Input type="number" value={formData.credit_limit} onChange={(e) => fv("credit_limit", e.target.value)} data-testid="basic-credit-limit" />
          </div>
          <div>
            <label className="text-sm font-medium text-slate-700">Google Location</label>
            <div className="flex gap-2">
              <Input value={formData.google_location} onChange={(e) => fv("google_location", e.target.value)} placeholder="Lat,Lng" data-testid="basic-gps" />
              <Button type="button" variant="outline" size="sm" onClick={() => {
                if (!navigator.geolocation) { toast.error("GPS not available"); return; }
                navigator.geolocation.getCurrentPosition(
                  (pos) => { fv("google_location", `${pos.coords.latitude.toFixed(6)},${pos.coords.longitude.toFixed(6)}`); toast.success("Location captured"); },
                  () => toast.error("Permission denied")
                );
              }} data-testid="capture-gps-btn"><Navigation className="w-3.5 h-3.5" /></Button>
            </div>
          </div>
          <div>
            <label className="text-sm font-medium text-slate-700">District</label>
            <Input value={formData.district} onChange={(e) => fv("district", e.target.value)} data-testid="basic-district" />
          </div>
          <div className="col-span-2">
            <label className="text-sm font-medium text-slate-700">Notes</label>
            <textarea value={formData.notes} onChange={(e) => fv("notes", e.target.value)} rows={2}
              className="w-full mt-1 px-3 py-2 border border-slate-200 rounded-md text-sm" placeholder="Internal notes..." data-testid="basic-notes" />
          </div>
          <div className="col-span-2">
            <label className="text-sm font-medium text-slate-700">Tags (comma separated)</label>
            <Input value={(formData.tags || []).join(", ")} onChange={(e) => fv("tags", e.target.value.split(",").map(t => t.trim()).filter(Boolean))} placeholder="vip, priority, bulk-buyer" data-testid="basic-tags" />
          </div>
        </div>

        {/* Delivery Addresses */}
        <div className="border-t pt-4">
          <div className="flex items-center justify-between mb-3">
            <label className="text-sm font-semibold text-slate-700">Delivery Addresses</label>
            <Button type="button" variant="outline" size="sm" onClick={addEditDelivery} data-testid="add-edit-delivery">
              <MapPinPlus className="w-3.5 h-3.5 mr-1" /> Add
            </Button>
          </div>
          <div className="space-y-2">
            {(formData.delivery_addresses || []).map((addr, i) => (
              <div key={i} className="flex gap-2 items-start" data-testid={`edit-delivery-${i}`}>
                <Input className="w-32" placeholder="Label" value={addr.label || ""} onChange={(e) => updateEditDelivery(i, "label", e.target.value)} />
                <Input className="flex-1" placeholder="Full address" value={addr.address || ""} onChange={(e) => updateEditDelivery(i, "address", e.target.value)} />
                {(formData.delivery_addresses || []).length > 1 && (
                  <Button type="button" variant="ghost" size="sm" className="h-8 w-8 p-0 text-red-500" onClick={() => removeEditDelivery(i)}>
                    <Trash2 className="w-3.5 h-3.5" />
                  </Button>
                )}
              </div>
            ))}
          </div>
        </div>

        {/* Photo upload (profile photo quick access) */}
        {pid && (
          <div className="border-t pt-4">
            <label className="text-sm font-semibold text-slate-700 block mb-2">Profile Photo</label>
            <FileUploadField label="Profile / Passport Photo" docType="profile_photo" partyId={pid}
              existingPath={editingParty?.profile_photo_path || docPaths["profile_photo_path"]}
              onUploaded={(p) => setDocPaths((d) => ({ ...d, profile_photo_path: p }))} />
          </div>
        )}
      </div>
    );
  };

  // ── BA Multi-Step Dialog ── <!-- retained below -->

  const stepRenderers = [renderBusinessStep, renderOwnerStep, renderBankStep, renderDocumentsStep, renderDeclarationStep];


  return (
    <div className="space-y-6" data-testid="dealers-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 font-['Sora']">Dealers & Distributors</h1>
          <p className="text-slate-500 text-sm mt-1">Manage your dealer and distributor network</p>
        </div>
        <div className="flex flex-wrap gap-2 items-center">
          <RegionSwitcher
            active={activeRegion}
            onChange={setActiveRegion}
            isSuperAdmin={isSuperAdmin}
            dataTestId="dealers-region-switcher"
          />
          <SyncNowButton entity="dealers" />
          {canEdit && (
            <>
              <Button onClick={() => { setQuickForm({ ...QUICK_FORM_INITIAL }); setIsQuickOpen(true); }}
                className="btn-primary" data-testid="add-dealer-btn">
                <Plus className="w-4 h-4 mr-2" /> Add Dealer
              </Button>
              <Button variant="outline" onClick={() => { setEditingParty(null); setFormData({ ...INITIAL_FORM }); setStep(0); setDocPaths({}); setSavedPartyId(null); setActiveEditTab("ba-form"); setIsBAOpen(true); }}
                className="border-[#8B1A1A] text-[#8B1A1A] hover:bg-[#8B1A1A]/5" data-testid="ba-form-btn">
                <FileText className="w-4 h-4 mr-2" /> Business Associate Form
              </Button>
            </>
          )}
        </div>
      </div>

      {/* Pending Approvals */}
      {pendingParties.length > 0 && (
        <Card className="p-4 border-amber-200 bg-amber-50" data-testid="pending-alert">
          <div className="flex items-center gap-3">
            <AlertCircle className="w-5 h-5 text-amber-600" />
            <div className="flex-1">
              <p className="font-medium text-amber-800">{pendingParties.length} pending approval{pendingParties.length > 1 ? "s" : ""}</p>
              <p className="text-sm text-amber-600">Review and approve new dealer registrations</p>
            </div>
            <Button variant="outline" size="sm" className="border-amber-300 text-amber-700 hover:bg-amber-100"
              onClick={() => setStatusFilter("PENDING")}>Review Now</Button>
          </div>
        </Card>
      )}

      {/* Filters */}
      <Card className="p-4">
        <div className="flex flex-col sm:flex-row gap-4">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <Input placeholder="Search by name, code, or mobile..." value={search} onChange={(e) => setSearch(e.target.value)} className="pl-10" data-testid="party-search" />
          </div>
          <Select value={typeFilter} onValueChange={setTypeFilter}>
            <SelectTrigger className="w-full sm:w-[180px]" data-testid="type-filter"><SelectValue placeholder="All Types" /></SelectTrigger>
            <SelectContent>
              {[["all","All Types"],["DEALER","Dealers"],["DISTRIBUTOR","Distributors"],["CONTRACTOR","Contractors"],["BUILDER","Builders"],["END_USER","End Users"]].map(([v,l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}
            </SelectContent></Select>
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="w-full sm:w-[180px]" data-testid="approval-filter"><SelectValue placeholder="All Status" /></SelectTrigger>
            <SelectContent>
              {[["all","All Status"],["APPROVED","Approved"],["PENDING","Pending"],["REJECTED","Rejected"]].map(([v,l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}
            </SelectContent></Select>
          {isSuperAdmin && (
            <Select value={regionFilter} onValueChange={setRegionFilter}>
              <SelectTrigger className="w-full sm:w-[160px]" data-testid="region-filter"><SelectValue placeholder="All Regions" /></SelectTrigger>
              <SelectContent><SelectItem value="all">All Regions</SelectItem><SelectItem value="GJ">Gujarat</SelectItem><SelectItem value="RJ">Rajasthan</SelectItem></SelectContent></Select>
          )}
        </div>
      </Card>

      {/* Table */}
      <Card className="overflow-hidden">
        <div className="overflow-x-auto">
          <table className="data-table" data-testid="parties-table">
            <thead><tr>
              <th>Party</th><th>Code</th><th>Type</th><th>Region</th><th>Assigned To</th><th>Credit Limit</th><th>Status</th><th className="w-12"></th>
            </tr></thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={8} className="text-center py-8"><div className="w-6 h-6 spinner mx-auto"></div></td></tr>
              ) : parties.length === 0 ? (
                <tr><td colSpan={8} className="text-center py-8">
                  <div className="empty-state"><Store className="empty-state-icon" /><p className="empty-state-title">No parties found</p><p className="empty-state-description">Add your first dealer.</p>
                    <Button onClick={() => setIsQuickOpen(true)} className="btn-primary"><Plus className="w-4 h-4 mr-2" /> Add Dealer</Button></div>
                </td></tr>
              ) : filterByRegion(parties, (p) => p.region_id).map((party) => (
                <tr key={party.party_id} data-testid={`party-row-${party.party_id}`}
                  className="cursor-pointer hover:bg-slate-50"
                  onClick={() => { setSelectedParty(party); setProfileOpen(true); }}>
                  <td>
                    <div className="flex items-center gap-3">
                      <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${party.party_type === "DISTRIBUTOR" ? "bg-purple-100" : "bg-blue-100"}`}>
                        <Store className={`w-5 h-5 ${party.party_type === "DISTRIBUTOR" ? "text-purple-600" : "text-blue-600"}`} />
                      </div>
                      <div>
                        <div className="flex items-center gap-1.5">
                          <p className="font-medium text-slate-900">{party.firm_name}</p>
                          {party.registration_type === "QUICK" && <Badge className="bg-blue-100 text-blue-700 text-[9px] px-1">QUICK</Badge>}
                        </div>
                        <p className="text-xs text-slate-500">{party.contact_person}</p>
                      </div>
                    </div>
                  </td>
                  <td className="font-mono text-sm">{party.party_code || "-"}</td>
                  <td><Badge variant="outline" className={party.party_type === "DISTRIBUTOR" ? "border-purple-200 text-purple-700 bg-purple-50" : "border-blue-200 text-blue-700 bg-blue-50"}>{party.party_type}</Badge></td>
                  <td>{party.region_id === "GJ" ? <Badge className="bg-[#C0272D]/10 text-[#C0272D] border-[#C0272D]/20 text-[10px]">Gujarat</Badge> : party.region_id === "RJ" ? <Badge className="bg-[#F5A623]/10 text-[#F5A623] border-[#F5A623]/20 text-[10px]">Rajasthan</Badge> : <span className="text-xs text-slate-400">-</span>}</td>
                  <td><span className="text-sm text-slate-600">{party.created_by_name || "\u2014"}</span></td>
                  <td className="numeric">{formatCurrency(party.credit_limit)}</td>
                  <td>{getApprovalBadge(party.approval_status)}</td>
                  <td onClick={(e) => e.stopPropagation()}>
                    {party.approval_status === "PENDING" ? (
                      <div className="flex items-center gap-1">
                        <Button variant="ghost" size="sm" className="h-8 w-8 p-0 text-emerald-600 hover:bg-emerald-50"
                          onClick={() => handleApprove(party.party_id)} data-testid={`approve-${party.party_id}`}><Check className="w-4 h-4" /></Button>
                        <Button variant="ghost" size="sm" className="h-8 w-8 p-0 text-red-600 hover:bg-red-50"
                          onClick={() => handleReject(party.party_id)} data-testid={`reject-${party.party_id}`}><X className="w-4 h-4" /></Button>
                      </div>
                    ) : (
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild><Button variant="ghost" size="sm" className="h-8 w-8 p-0"><MoreHorizontal className="w-4 h-4" /></Button></DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem onClick={() => openEditDialog(party)}><Edit className="w-4 h-4 mr-2" /> Edit</DropdownMenuItem>
                          <DropdownMenuItem onClick={() => setCataloguePartyId(party.party_id)} data-testid={`catalogue-${party.party_id}`}>
                            <BookOpen className="w-4 h-4 mr-2" /> View Catalogue
                          </DropdownMenuItem>
                          {party.registration_type === "QUICK" && (
                            <DropdownMenuItem onClick={() => openBAForQuickDealer(party)}><FileText className="w-4 h-4 mr-2" /> Complete BA Form</DropdownMenuItem>
                          )}
                          <DropdownMenuItem onClick={() => setQrParty(party)}><QrCode className="w-4 h-4 mr-2" /> QR Code</DropdownMenuItem>
                          {isSuperAdmin && (
                            <DropdownMenuItem
                              onClick={() => handleDeleteParty(party)}
                              className="text-red-600 focus:text-red-700 focus:bg-red-50"
                              data-testid={`delete-party-${party.party_id}`}
                            >
                              <Trash2 className="w-4 h-4 mr-2" /> Delete (deactivate)
                            </DropdownMenuItem>
                          )}
                        </DropdownMenuContent>
                      </DropdownMenu>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>

      {/* ── Quick Add Dealer Dialog ── */}
      <Dialog open={isQuickOpen} onOpenChange={setIsQuickOpen}>
        <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto" data-testid="quick-dealer-dialog">
          <DialogHeader><DialogTitle>Add New Dealer</DialogTitle></DialogHeader>
          <form onSubmit={handleQuickSubmit} className="space-y-4">
            <DynamicFormRenderer entityType="dealer" formData={quickForm} setFormData={setQuickForm} sectionFilter="business" />

            {/* Region (always shown) */}
            <div>
              <label className="text-sm font-medium text-slate-700">Region</label>
              <Select value={quickForm.region_id || ""} onValueChange={(v) => setQuickForm(f => ({ ...f, region_id: v }))}>
                <SelectTrigger data-testid="quick-region"><SelectValue placeholder="Select" /></SelectTrigger>
                <SelectContent><SelectItem value="GJ">Gujarat</SelectItem><SelectItem value="RJ">Rajasthan</SelectItem></SelectContent></Select></div>

            {/* Delivery Addresses */}
            <div className="border-t pt-4">
              <div className="flex items-center justify-between mb-3">
                <label className="text-sm font-semibold text-slate-700">Delivery Addresses</label>
                <Button type="button" variant="outline" size="sm" onClick={addDeliveryAddress} data-testid="add-delivery-addr">
                  <MapPinPlus className="w-3.5 h-3.5 mr-1" /> Add Address
                </Button>
              </div>
              <div className="space-y-3">
                {quickForm.delivery_addresses.map((addr, i) => (
                  <div key={i} className="flex gap-2 items-start" data-testid={`delivery-addr-${i}`}>
                    <div className="flex-1 space-y-2">
                      <div className="flex gap-2">
                        <Input className="w-32" placeholder="Label" value={addr.label}
                          onChange={(e) => updateDeliveryAddress(i, "label", e.target.value)} data-testid={`addr-label-${i}`} />
                        <Input className="flex-1" placeholder="Full delivery address" value={addr.address}
                          onChange={(e) => updateDeliveryAddress(i, "address", e.target.value)} data-testid={`addr-value-${i}`} />
                      </div>
                    </div>
                    {quickForm.delivery_addresses.length > 1 && (
                      <Button type="button" variant="ghost" size="sm" className="h-8 w-8 p-0 text-red-500 mt-0.5"
                        onClick={() => removeDeliveryAddress(i)} data-testid={`remove-addr-${i}`}>
                        <Trash2 className="w-3.5 h-3.5" />
                      </Button>
                    )}
                  </div>
                ))}
              </div>
            </div>

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setIsQuickOpen(false)}>Cancel</Button>
              <Button type="submit" className="btn-primary" data-testid="submit-quick-dealer">Add Dealer</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* ── Edit Dialog with Tabs (Basic Details / BA Form) ── */}
      <Dialog open={isBAOpen} onOpenChange={(open) => { if (!open) closeBA(); }}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="ba-form-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-lg">
              {editingParty ? `Edit: ${editingParty.firm_name}` : "New Dealer / Distributor"}
              {(editingParty || savedPartyId) && (
                <Button type="button" variant="outline" size="sm" onClick={downloadBAFormPDF} className="ml-auto" data-testid="download-ba-pdf">
                  <Download className="w-3.5 h-3.5 mr-1" /> Download BA Form PDF
                </Button>
              )}
            </DialogTitle>
          </DialogHeader>

          <Tabs value={activeEditTab} onValueChange={setActiveEditTab} className="mt-2">
            <TabsList className="grid grid-cols-2 w-full">
              <TabsTrigger value="basic" data-testid="tab-basic-details"><User className="w-4 h-4 mr-1.5" /> Basic Details</TabsTrigger>
              <TabsTrigger value="ba-form" data-testid="tab-ba-form"><FileText className="w-4 h-4 mr-1.5" /> Business Associate Form</TabsTrigger>
            </TabsList>

            <TabsContent value="basic" className="mt-4">
              {renderBasicDetailsTab()}
              <DialogFooter className="pt-4 border-t mt-4">
                <Button type="button" variant="outline" onClick={closeBA}>Cancel</Button>
                <Button type="button" className="btn-primary" onClick={saveBasicDetails} disabled={basicSaving} data-testid="save-basic-btn">
                  {basicSaving ? "Saving..." : <><Check className="w-4 h-4 mr-1" /> Save Basic Details</>}
                </Button>
              </DialogFooter>
            </TabsContent>

            <TabsContent value="ba-form" className="mt-4">
              <div className="flex items-center justify-between mb-2">
                <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide">Full KYC / Business Associate Application</p>
                <span className="text-xs font-normal text-slate-400">Step {step + 1} of {STEPS.length}</span>
              </div>
              <Progress value={((step + 1) / STEPS.length) * 100} className="h-1 mb-3" />
              <StepIndicator current={step} onStepClick={setStep} />
              <div className="min-h-[320px]">{stepRenderers[step]()}</div>
              <DialogFooter className="flex justify-between gap-2 pt-4 border-t mt-4">
                <div>{step > 0 && <Button type="button" variant="outline" onClick={() => setStep(s => s - 1)} data-testid="btn-prev"><ChevronLeft className="w-4 h-4 mr-1" /> Back</Button>}</div>
                <div className="flex gap-2">
                  <Button type="button" variant="outline" onClick={closeBA}>Cancel</Button>
                  {step < STEPS.length - 1 ? (
                    <Button type="button" className="btn-primary" onClick={handleNext} data-testid="btn-next">Save & Continue <ChevronRight className="w-4 h-4 ml-1" /></Button>
                  ) : (
                    <Button type="button" className="btn-primary" onClick={handleFinalSubmit} data-testid="submit-party" disabled={!formData.declaration_accepted}><Check className="w-4 h-4 mr-1" /> Submit Application</Button>
                  )}
                </div>
              </DialogFooter>
            </TabsContent>
          </Tabs>
        </DialogContent>
      </Dialog>

      {/* ── Dealer Profile View ── */}
      <Dialog open={profileOpen} onOpenChange={setProfileOpen}>
        <DialogContent className="max-w-xl max-h-[85vh] overflow-y-auto" data-testid="dealer-profile-dialog">
          {selectedParty && (
            <>
              <DialogHeader>
                <div className="flex items-center gap-4">
                  <div className={`w-14 h-14 rounded-lg flex items-center justify-center ${selectedParty.party_type === "DEALER" ? "bg-emerald-100" : "bg-blue-100"}`}>
                    <Store className={`w-6 h-6 ${selectedParty.party_type === "DEALER" ? "text-emerald-600" : "text-blue-600"}`} />
                  </div>
                  <div className="flex-1">
                    <div className="flex items-center gap-2">
                      <DialogTitle className="text-lg">{selectedParty.firm_name}</DialogTitle>
                      {selectedParty.registration_type === "QUICK" && <Badge className="bg-blue-100 text-blue-700 text-[9px]">QUICK</Badge>}
                    </div>
                    <p className="text-sm text-slate-500">{selectedParty.party_code} &middot; {selectedParty.party_type}</p>
                  </div>
                  <div className="flex gap-1.5 flex-wrap">
                    {getApprovalBadge(selectedParty.approval_status)}
                    {selectedParty.google_location && <Button size="sm" variant="outline" onClick={() => window.open(`https://www.google.com/maps?q=${selectedParty.google_location}`, "_blank")}><MapPin className="w-3 h-3 mr-1" /> Map</Button>}
                    {selectedParty.address && <Button size="sm" variant="outline" onClick={() => window.open(`https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(selectedParty.address + " " + (selectedParty.district || ""))}`, "_blank")}><Navigation className="w-3 h-3 mr-1" /> Directions</Button>}
                    <Button size="sm" variant="outline" onClick={() => { setProfileOpen(false); openEditDialog(selectedParty); }}><Edit className="w-3 h-3 mr-1" /> Edit</Button>
                    <Button size="sm" variant="outline" onClick={() => setLoyaltyParty(selectedParty)} className="border-[#8B1A1A] text-[#8B1A1A] hover:bg-[#8B1A1A]/5" data-testid="view-loyalty-btn">
                      <Gift className="w-3 h-3 mr-1" /> Loyalty
                    </Button>
                    {selectedParty.registration_type === "QUICK" && <Button size="sm" className="bg-[#8B1A1A] text-white hover:bg-[#6B1515]" onClick={() => openBAForQuickDealer(selectedParty)}><FileText className="w-3 h-3 mr-1" /> Complete BA Form</Button>}
                  </div>
                </div>
              </DialogHeader>
              <div className="space-y-4 mt-2">
                <div className="grid grid-cols-3 gap-3 text-sm">
                  <DField label="Contact Person" value={selectedParty.contact_person} />
                  <DField label="Mobile" value={selectedParty.mobile_number} />
                  <DField label="Email" value={selectedParty.email} />
                  <DField label="Address" value={selectedParty.address} />
                  <DField label="District" value={selectedParty.district} />
                  <DField label="Category" value={selectedParty.category} />
                  <DField label="Credit Limit" value={selectedParty.credit_limit ? formatCurrency(selectedParty.credit_limit) : "\u2014"} />
                  <DField label="Year Est." value={selectedParty.year_of_establishment} />
                  <DField label="Entity Type" value={selectedParty.entity_type} />
                  <DField label="GSTIN" value={selectedParty.gstin} />
                </div>
                {/* Delivery Addresses */}
                {selectedParty.delivery_addresses?.length > 0 && (
                  <div className="border-t pt-3">
                    <p className="text-xs font-semibold text-slate-500 uppercase mb-2">Delivery Addresses</p>
                    <div className="space-y-2">
                      {selectedParty.delivery_addresses.map((addr, i) => (
                        <div key={i} className="flex items-start gap-2 text-sm">
                          <MapPin className="w-3.5 h-3.5 text-[#8B1A1A] mt-0.5 shrink-0" />
                          <div>
                            <span className="font-medium text-slate-700">{addr.label || `Address ${i+1}`}: </span>
                            <span className="text-slate-600">{addr.address || "\u2014"}</span>
                            {addr.is_default && <Badge className="ml-2 bg-emerald-100 text-emerald-700 text-[9px]">Default</Badge>}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
                {/* Owner */}
                <div className="border-t pt-3">
                  <p className="text-xs font-semibold text-slate-500 uppercase mb-2">Owner / Proprietor</p>
                  <div className="grid grid-cols-3 gap-3 text-sm">
                    <DField label="Father/Husband" value={selectedParty.father_husband_name} />
                    <DField label="Blood Group" value={selectedParty.owner_blood_group} />
                    <DField label="Aadhar" value={selectedParty.owner_aadhar_number} />
                    <DField label="PAN" value={selectedParty.owner_pan_number} />
                    <DField label="Home Address" value={selectedParty.owner_home_address} />
                    <DField label="State" value={selectedParty.state} />
                  </div>
                </div>
                {/* Banking */}
                <div className="border-t pt-3">
                  <p className="text-xs font-semibold text-slate-500 uppercase mb-2">Banking</p>
                  <div className="grid grid-cols-3 gap-3 text-sm">
                    <DField label="Bank Name" value={selectedParty.bank_name} />
                    <DField label="Branch" value={selectedParty.bank_branch} />
                    <DField label="Account No." value={selectedParty.bank_account_number} />
                    <DField label="IFSC" value={selectedParty.ifsc_code} />
                    <DField label="Signatory" value={selectedParty.bank_signatory_name} />
                  </div>
                </div>
                {/* Documents */}
                <div className="border-t pt-3">
                  <p className="text-xs font-semibold text-slate-500 uppercase mb-2">Documents</p>
                  <div className="grid grid-cols-3 gap-3 text-sm">
                    {[["PAN Card","pan_card_path"],["GST Cert","gst_certificate_path"],["Aadhar","aadhar_card_path"],["Photo ID","photo_id_path"],["Letterhead","letterhead_path"],["Bank Stmt","bank_statement_path"],["Partnership","partnership_deed_path"],["Cheque","cancelled_cheque_path"]].map(([l,k]) => <DocIndicator key={k} label={l} path={selectedParty[k]} />)}
                  </div>
                </div>
                {/* Assignment */}
                <div className="border-t pt-3">
                  <p className="text-xs font-semibold text-slate-500 uppercase mb-2">Assignment</p>
                  <div className="grid grid-cols-2 gap-3 text-sm">
                    <DField label="Created By" value={selectedParty.created_by_name} />
                    <DField label="Team" value={selectedParty.team_name} />
                    <DField label="Declaration" value={selectedParty.declaration_accepted ? "Accepted" : "Not accepted"} />
                    <DField label="Registration" value={selectedParty.registration_type || "FULL"} />
                  </div>
                </div>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* ── Dealer Loyalty Dialog ── */}
      <Dialog open={!!loyaltyParty} onOpenChange={(o) => { if (!o) setLoyaltyParty(null); }}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="loyalty-dialog">
          {loyaltyParty && (
            <>
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2">
                  <Gift className="w-5 h-5 text-[#8B1A1A]" /> Loyalty & Rewards — {loyaltyParty.firm_name}
                </DialogTitle>
              </DialogHeader>
              <DealerLoyaltyCard partyId={loyaltyParty.party_id} firmName={loyaltyParty.firm_name} />
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* ── QR Code Dialog ── */}
      <Dialog open={!!qrParty} onOpenChange={(o) => { if (!o) setQrParty(null); }}>
        <DialogContent className="max-w-sm" data-testid="qr-dialog">
          {qrParty && (
            <>
              <DialogHeader><DialogTitle>QR Code - {qrParty.firm_name}</DialogTitle></DialogHeader>
              <div className="flex flex-col items-center gap-4 py-4">
                <div className="w-48 h-48 bg-slate-100 rounded-lg flex items-center justify-center border-2 border-dashed border-slate-300">
                  <img src={`https://api.qrserver.com/v1/create-qr-code/?size=180x180&data=${encodeURIComponent(generateQRUrl(qrParty))}`}
                    alt="QR Code" className="w-44 h-44" data-testid="qr-image" />
                </div>
                <p className="text-xs text-slate-500 text-center">Scan to open pre-filled dealer inquiry form</p>
                <Button variant="outline" size="sm" onClick={() => {
                  navigator.clipboard.writeText(generateQRUrl(qrParty));
                  toast.success("Link copied!");
                }} data-testid="copy-qr-link">
                  <Copy className="w-3.5 h-3.5 mr-1" /> Copy Link
                </Button>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* ── Dealer Catalogue Dialog (P2 iter81) ── */}
      {cataloguePartyId && (
        <DealerCatalogueDialog
          open={!!cataloguePartyId}
          onClose={() => setCataloguePartyId(null)}
          partyId={cataloguePartyId}
        />
      )}
    </div>
  );
};

const DField = ({ label, value }) => (
  <div data-testid={`profile-field-${label.toLowerCase().replace(/\s+/g, "-")}`}>
    <p className="text-slate-400 text-xs">{label}</p>
    <p className="text-slate-800 font-medium">{value || <span className="text-slate-300">{"\u2014"}</span>}</p>
  </div>
);

const DocIndicator = ({ label, path }) => (
  <div data-testid={`doc-${label.toLowerCase().replace(/\s+/g, "-")}`}>
    <p className="text-slate-400 text-xs">{label}</p>
    <div className="flex items-center gap-1">
      {path ? <><FileCheck className="w-3.5 h-3.5 text-emerald-600" /><span className="text-xs text-emerald-600 font-medium">Uploaded</span></> :
        <><X className="w-3.5 h-3.5 text-slate-300" /><span className="text-xs text-slate-400">Not uploaded</span></>}
    </div>
  </div>
);

export default DealersPage;
